import { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { setSessionExpiredHandler } from "../api";

/* Global "Session expired" modal.
 *
 * Why this exists:
 *   - JWTs do expire eventually (after 7 days of idleness).
 *   - Without this modal, an expired token kicked the user out to /login
 *     mid-form, losing their unsaved work.
 *   - This component listens for a single global "session-expired" signal
 *     from the axios response interceptor. When triggered, it renders a
 *     blocking modal asking for the password ONLY (email is remembered).
 *   - On successful re-auth, the modal closes and the user picks up
 *     exactly where they left off — the form they were filling, the
 *     dispatch they were creating, etc.
 */
export default function SessionExpiredModal() {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pwRef = useRef(null);

  useEffect(() => {
    // Register a callback with the api interceptor so 401s flip this open.
    setSessionExpiredHandler(() => {
      // Only show if we actually have a user session in memory — otherwise
      // ProtectedRoute will bounce them to /login normally (initial load).
      if (user && user.email) {
        setErr("");
        setPassword("");
        setOpen(true);
      }
    });
    return () => setSessionExpiredHandler(null);
  }, [user]);

  useEffect(() => {
    if (open && pwRef.current) setTimeout(() => pwRef.current.focus(), 50);
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) { setErr("Enter password"); return; }
    setBusy(true); setErr("");
    try {
      await login(user.email, password);
      setOpen(false);
      setPassword("");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="session-expired-modal"
    >
      <div className="bg-white border border-zinc-300 shadow-2xl w-[400px] max-w-[92vw] p-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Session check
        </div>
        <div className="text-lg font-bold text-zinc-950 mb-1">Welcome back, {user?.name || user?.email}</div>
        <div className="text-xs text-zinc-600 mb-5 leading-relaxed">
          Your session expired. Enter your password to continue —
          <span className="font-semibold"> your unsaved work is still here.</span>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Email
          </div>
          <div className="font-mono text-sm text-zinc-800 border border-zinc-200 bg-zinc-50 px-3 py-1.5">
            {user?.email}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-3">
            Password
          </div>
          <input
            ref={pwRef}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950"
            autoComplete="current-password"
            data-testid="session-expired-password"
          />
          {err && <div className="text-xs text-red-700">{err}</div>}
          <div className="pt-2">
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-zinc-950 text-white text-sm font-semibold py-2 hover:bg-zinc-800 disabled:bg-zinc-400"
              data-testid="session-expired-submit"
            >
              {busy ? "Signing in…" : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
